import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { toast } from "sonner";
import { 
  Shield, 
  Users, 
  Home,
  Building2,
  Landmark,
  Hammer,
  Eye,
  ArrowRight,
  Activity,
  Clock,
  CheckCircle,
  AlertCircle,
  UserCheck,
  ChevronRight,
  X,
  LogOut,
  User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Role configurations for the portals
const ROLE_PORTALS = [
  {
    key: 'realtor',
    label: 'Realtor',
    description: 'Premium Realtor Network & Lead Marketplace',
    icon: Home,
    color: '#3B82F6',
    path: '/realtor/dashboard',
    features: ['Bid on deals', 'Manage listings', 'Track performance', 'Lead marketplace']
  },
  {
    key: 'title',
    label: 'Title Company',
    description: 'Title Processing & Closing Coordination',
    icon: Building2,
    color: '#8B5CF6',
    path: '/title/dashboard',
    features: ['Process title orders', 'Manage closings', 'Document management', 'Fee tracking']
  },
  {
    key: 'lender',
    label: 'Lender',
    description: 'Capital Deployment & Loan Management',
    icon: Landmark,
    color: '#10B981',
    path: '/lender/portal',
    features: ['Fund deals', 'Manage portfolio', 'Track returns', 'Loan servicing']
  },
  {
    key: 'contractor',
    label: 'Contractor',
    description: 'Rehab Projects & Bid Management',
    icon: Hammer,
    color: '#F59E0B',
    path: '/contractor/dashboard',
    features: ['Submit bids', 'Manage projects', 'Track payments', 'Scope management']
  }
];

export default function AdminPortalAccess({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const { mainClass } = useSidebar();
  const [portalStats, setPortalStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [recentActivity, setRecentActivity] = useState([]);
  
  // Impersonation state
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [roleUsers, setRoleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [impersonationStatus, setImpersonationStatus] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchPortalStats();
    checkImpersonationStatus();
  }, []);

  const fetchPortalStats = async () => {
    try {
      // Fetch stats for each portal type
      const [realtorsRes, titleRes, lendersRes, contractorsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/realtors`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/title-companies`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/lenders`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/contractors`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);

      setPortalStats({
        realtor: { count: realtorsRes.data?.length || 0, active: realtorsRes.data?.filter(r => r.status === 'active').length || 0 },
        title: { count: titleRes.data?.length || 0, active: titleRes.data?.filter(t => t.status === 'active').length || 0 },
        lender: { count: lendersRes.data?.length || 0, active: lendersRes.data?.filter(l => l.status === 'active').length || 0 },
        contractor: { count: contractorsRes.data?.length || 0, active: contractorsRes.data?.filter(c => c.status === 'active').length || 0 }
      });

      // Generate recent activity from the data
      const activity = [];
      if (realtorsRes.data?.length > 0) {
        const recent = realtorsRes.data.slice(0, 2);
        recent.forEach(r => activity.push({
          type: 'realtor',
          name: r.name || r.company_name,
          action: 'registered',
          time: r.created_at
        }));
      }
      if (contractorsRes.data?.length > 0) {
        const recent = contractorsRes.data.slice(0, 2);
        recent.forEach(c => activity.push({
          type: 'contractor',
          name: c.company_name || c.contact_name,
          action: 'submitted bid',
          time: c.created_at
        }));
      }
      setRecentActivity(activity.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch portal stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkImpersonationStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/impersonation-status`, { withCredentials: true });
      setImpersonationStatus(res.data);
    } catch (error) {
      console.error("Failed to check impersonation status:", error);
    }
  };

  const fetchUsersForRole = async (role) => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/users-by-role/${role}`, { withCredentials: true });
      setRoleUsers(res.data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users for this role");
      setRoleUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handlePortalClick = async (portal) => {
    setSelectedPortal(portal);
    await fetchUsersForRole(portal.key);
  };

  const handleImpersonate = async (targetUser) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/impersonate/${targetUser.user_id}`,
        {},
        { withCredentials: true }
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        setSelectedPortal(null);
        // Navigate to the portal
        const portal = ROLE_PORTALS.find(p => p.key === selectedPortal.key);
        if (portal) {
          navigate(portal.path);
        }
      }
    } catch (error) {
      toast.error("Failed to start impersonation");
    }
  };

  const handleStopImpersonation = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/stop-impersonation`, {}, { withCredentials: true });
      toast.success("Impersonation ended");
      setImpersonationStatus(null);
      navigate('/admin/portals');
    } catch (error) {
      toast.error("Failed to stop impersonation");
    }
  };

  const handlePortalAccess = (portal) => {
    toast.success(`Accessing ${portal.label} Portal as Admin`);
    navigate(portal.path);
  };

  // Access check
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Access Denied</h1>
          <p className="text-white/50 mb-6">Admin privileges required.</p>
          <Link to="/dashboard">
            <button className="px-6 py-2.5 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold rounded-sm hover:opacity-90 transition-opacity">
              Return to Dashboard
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const totalUsers = Object.values(portalStats).reduce((sum, p) => sum + (p?.count || 0), 0);
  const activeUsers = Object.values(portalStats).reduce((sum, p) => sum + (p?.active || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass} data-testid="admin-portal-access">
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm">
          <div className="px-4 md:px-6 py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4 md:gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500/[0.08] border border-red-500/20 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#FF3B30]" />
                  </div>
                  <div>
                    <h1 className="text-base md:text-lg font-semibold tracking-tight">
                      Admin <span className="text-[#FF3B30]">Portal Access</span>
                    </h1>
                    <p className="text-[9px] text-white/50 uppercase tracking-[0.15em] hidden md:block">Role Impersonation & Testing</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-red-500/[0.08] border border-red-500/20 rounded">
                  <Shield className="w-3 h-3 text-[#FF3B30]" />
                  <span className="text-[10px] font-semibold text-[#FF3B30] uppercase tracking-wider">Admin</span>
                </div>
              </div>

              <div className="hidden lg:flex items-center gap-8">
                <div>
                  <div className="text-[9px] font-medium text-white/50 uppercase tracking-[0.12em] mb-1">Total Partners</div>
                  <span className="text-xl font-light text-white">{totalUsers}</span>
                </div>
                <div className="w-px h-10 bg-white/[0.04]" />
                <div>
                  <div className="text-[9px] font-medium text-white/50 uppercase tracking-[0.12em] mb-1">Active</div>
                  <span className="text-xl font-light text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">{activeUsers}</span>
                </div>
                <div className="w-px h-10 bg-white/[0.04]" />
                <div>
                  <div className="text-[9px] font-medium text-white/50 uppercase tracking-[0.12em] mb-1">Portals</div>
                  <span className="text-xl font-light text-[#D4AF37]">4</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6">
          {/* Portal Access Cards */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Role Portals</h2>
              <span className="text-[10px] text-white/40">Click to access each portal as admin</span>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {ROLE_PORTALS.map((portal) => {
                  const stats = portalStats[portal.key] || { count: 0, active: 0 };
                  const Icon = portal.icon;
                  
                  return (
                    <button
                      key={portal.key}
                      onClick={() => handlePortalClick(portal)}
                      className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6 text-left hover:border-white/[0.15] hover:bg-white/[0.02] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] transition-all group"
                      data-testid={`portal-${portal.key}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ background: `${portal.color}15`, borderColor: `${portal.color}30`, borderWidth: '1px' }}
                        >
                          <Icon className="w-6 h-6" style={{ color: portal.color }} />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-mono font-bold text-white">{stats.count}</span>
                          <span className="text-[10px] text-white/40">registered</span>
                        </div>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-[#D4AF37] transition-all duration-300">
                        {portal.label} Portal
                      </h3>
                      <p className="text-sm text-white/50 mb-4">{portal.description}</p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {portal.features.map((feature, idx) => (
                          <span 
                            key={idx}
                            className="px-2 py-1 text-[10px] bg-white/[0.02] border border-white/[0.06] rounded text-white/50"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
                        <div className="flex items-center gap-2">
                          {stats.active > 0 ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                              <span className="text-[11px] text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">{stats.active} active</span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-4 h-4 text-white/40" />
                              <span className="text-[11px] text-white/40">No active users</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity">
                          <Eye className="w-4 h-4" />
                          <span className="text-[11px] font-semibold">Access Portal</span>
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-3 gap-4">
            {/* Admin Dashboard */}
            <Link 
              to="/admin"
              className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5 hover:border-red-500/30 transition-all duration-300 group"
            >
              <div className="flex items-center justify-between mb-3">
                <Shield className="w-5 h-5 text-[#FF3B30]" />
                <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-[#FF3B30] transition-all duration-300" />
              </div>
              <h3 className="text-sm font-medium text-white mb-1">Admin Dashboard</h3>
              <p className="text-[11px] text-white/40">Platform analytics & metrics</p>
            </Link>
            
            {/* User Management */}
            <Link 
              to="/admin/users"
              className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5 hover:border-[#D4AF37]/30 transition-all duration-300 group"
            >
              <div className="flex items-center justify-between mb-3">
                <Users className="w-5 h-5 text-[#D4AF37]" />
                <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-[#D4AF37] transition-all duration-300" />
              </div>
              <h3 className="text-sm font-medium text-white mb-1">User Management</h3>
              <p className="text-[11px] text-white/40">View & manage all users</p>
            </Link>
            
            {/* Waitlist Management */}
            <Link 
              to="/admin/waitlist"
              className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5 hover:border-emerald-500/30 transition-all duration-300 group"
            >
              <div className="flex items-center justify-between mb-3">
                <UserCheck className="w-5 h-5 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] transition-all duration-300" />
              </div>
              <h3 className="text-sm font-medium text-white mb-1">Waitlist</h3>
              <p className="text-[11px] text-white/40">Manage beta signups</p>
            </Link>
          </div>

          {/* Recent Activity */}
          {recentActivity.length > 0 && (
            <div className="mt-6 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg">
              <div className="px-5 py-4 border-b border-white/[0.06]">
                <h3 className="text-sm font-semibold text-white">Recent Partner Activity</h3>
              </div>
              <div className="divide-y divide-white/[0.02]">
                {recentActivity.map((item, idx) => {
                  const portal = ROLE_PORTALS.find(p => p.key === item.type);
                  const Icon = portal?.icon || Activity;
                  
                  return (
                    <div key={idx} className="px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4" style={{ color: portal?.color || '#D4AF37' }} />
                        <div>
                          <span className="text-sm text-white">{item.name}</span>
                          <span className="text-sm text-white/40 ml-2">{item.action}</span>
                        </div>
                      </div>
                      <span className="text-[10px] text-white/40">
                        {item.time ? new Date(item.time).toLocaleDateString() : 'Recently'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* User Selection Modal for Impersonation */}
      {selectedPortal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0F0F0F] border border-white/[0.1] rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {selectedPortal.label} Portal Access
                </h2>
                <p className="text-sm text-white/50 mt-1">
                  Select a user to impersonate or view as admin
                </p>
              </div>
              <button
                onClick={() => setSelectedPortal(null)}
                className="p-2 hover:bg-white/[0.02] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] rounded-lg transition-all duration-300"
              >
                <X className="w-5 h-5 text-white/50" />
              </button>
            </div>

            {/* User List */}
            <div className="max-h-[400px] overflow-y-auto">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : roleUsers.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <User className="w-12 h-12 text-white/30 mx-auto mb-3" />
                  <p className="text-white/50">No users found for this role</p>
                  <button
                    onClick={() => {
                      handlePortalAccess(selectedPortal);
                      setSelectedPortal(null);
                    }}
                    className="mt-4 px-4 py-2 bg-[#D4AF37] text-black text-sm font-medium rounded hover:bg-[#B8972E] transition-all duration-300"
                  >
                    View Portal Anyway
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-white/[0.04]">
                  {roleUsers.map((u) => (
                    <button
                      key={u.user_id}
                      onClick={() => handleImpersonate(u)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-all duration-300 text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                          <span className="text-[#D4AF37] font-medium">
                            {u.name ? u.name.charAt(0).toUpperCase() : u.email?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{u.name || 'Unnamed User'}</p>
                          <p className="text-[11px] text-white/40">{u.email}</p>
                          {u.company_name && (
                            <p className="text-[10px] text-white/30 mt-0.5">{u.company_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[#D4AF37]">
                        <Eye className="w-4 h-4" />
                        <span className="text-[11px] font-medium">Impersonate</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-between bg-[#0A0A0A]/80 backdrop-blur-sm">
              <button
                onClick={() => setSelectedPortal(null)}
                className="px-4 py-2 text-sm text-white/50 hover:text-white transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handlePortalAccess(selectedPortal);
                  setSelectedPortal(null);
                }}
                className="px-4 py-2 bg-white/[0.04] border border-white/[0.1] text-sm text-white rounded hover:bg-white/[0.08] transition-all duration-300"
              >
                View as Admin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Impersonation Banner */}
      {impersonationStatus?.is_impersonating && (
        <div className="fixed bottom-0 left-0 right-0 bg-amber-500/90 backdrop-blur-sm z-50">
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-black" />
              <span className="text-sm font-medium text-black">
                Impersonating: {impersonationStatus.impersonated_user?.email}
              </span>
            </div>
            <button
              onClick={handleStopImpersonation}
              className="flex items-center gap-2 px-4 py-1.5 bg-black/20 rounded text-black text-sm font-medium hover:bg-black/30 transition-all duration-300"
            >
              <LogOut className="w-4 h-4" />
              Stop Impersonation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
